import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Pin, Plus, Tag as TagIcon, Trash2, Zap } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, PageHead, Section, TONES } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { money } from "@/vault/marketplace/Marketplace";

const TONE = TONES.listings;
const SHOW = [
    { key: "all", label: "Everything", match: () => true },
    { key: "active", label: "Live", match: (l) => l.status === "active" },
    { key: "pending", label: "In review", match: (l) => l.status === "pending" },
    { key: "sold", label: "Sold", match: (l) => l.status === "sold" },
    { key: "other", label: "Removed / sent back", match: (l) => !["active", "pending", "sold"].includes(l.status) },
];
const COLS = [
    { key: "photo", label: "Photo", w: "64px" },
    { key: "title", label: "Ad", sub: "click the title to see it on the board", w: "minmax(220px,1.8fr)" },
    { key: "price", label: "Price", w: "100px" },
    { key: "views", label: "Views", w: "70px" },
    { key: "inquiries", label: "Inquiries", sub: "buyers who wrote in", w: "90px" },
    { key: "status", label: "Status", sub: "in review · live · sold", w: "110px" },
    { key: "boost", label: "Boost", sub: "$1.98 · top of its tab for 7 days", w: "130px" },
    { key: "pin", label: "Bulletin board", sub: "pin to the homepage for 24h", w: "130px" },
    { key: "actions", label: "Actions", sub: "mark sold · relist · remove", w: "170px" },
];
const MSG_COLS = [
    { key: "who", label: "From", sub: "name · contact", w: "minmax(200px,1.2fr)" },
    { key: "ad", label: "About", sub: "the ad they asked on", w: "minmax(180px,1fr)" },
    { key: "message", label: "Message", w: "minmax(300px,2.4fr)" },
];

/** Classifieds — the shop's own ads on the mall bulletin board, and Post an ad lives right here. */
export default function MyListings() {
    const [listings, setListings] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState("all");

    const load = () => {
        setLoading(true);
        Promise.all([api.get("/marketplace/my/listings"), api.get("/marketplace/my/inquiries")])
            .then(([a, b]) => { setListings(a.data.listings); setInquiries(b.data.inquiries); })
            .catch(() => { }).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const act = async (fn, msg) => {
        try { await fn(); if (msg) toast.success(msg); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const boost = async (l) => {
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key: "boost_featured_7d", origin_url: window.location.origin, listing_id: l.id,
            });
            window.location.href = data.checkout_url;
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const status = (l, s, msg) => act(() => api.post(`/marketplace/listings/${l.id}/status?status=${s}`), msg);

    const live = listings.filter((l) => l.status === "active").length;
    const shown = listings.filter(SHOW.find((s) => s.key === show).match);

    return (
        <BackOffice>
            <PageHead kicker="Listings" tone={TONE} title="Classifieds" testid="my-listings-title"
                sub="Your ads on the shared bulletin board — machines, supplies, blanks, wanted ads. Free to post, no commission, buyers message you direct."
                right={
                    <div className="text-right">
                        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">Live / total</p>
                        <p className="font-mono text-2xl font-black tabular-nums text-zinc-900" data-testid="my-listings-count">
                            {live}<span className="text-zinc-600">/{listings.length}</span>
                        </p>
                        <div className="mt-1.5">
                            <Link to="/supplies/sell"><Btn tone="solid" testid="new-listing-btn"><Plus size={11} /> post an ad</Btn></Link>
                        </div>
                    </div>
                } />

            {loading ? (
                <div className="flex min-h-[40vh] items-center justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>
            ) : (
                <>
                    <Filters testid="my-listings-filters">
                        <PillRow label="Show" testid="listings-show" value={show} onPick={setShow}
                            choices={SHOW.map((s) => ({ key: s.key, label: s.label, count: listings.filter(s.match).length }))} />
                    </Filters>

                    <div className="mt-3 space-y-3">
                        <Section tone={TONE} pad={false} testid="my-listings" title={`${shown.length} ads`}
                            note="new ads go to 13thav support for a look before they go live">
                            <Sheet cols={COLS} rows={shown} rowKey={(l) => l.id} testid="listings-sheet" minWidth={1180}
                                rowTestid={(l) => `my-listing-${l.slug}`}
                                empty={listings.length ? "Nothing matches that filter." : "Nothing pinned up yet. Selling a machine, film, powder or blanks? Post it free."}
                                cells={(l) => ({
                                    photo: (
                                        <Cell>
                                            <span className="h-8 w-10 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                                {l.images?.[0] && <img src={mediaUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}
                                            </span>
                                        </Cell>
                                    ),
                                    title: <Cell><Link to={`/supplies/${l.slug}`} className="truncate text-[12px] font-semibold text-[#1a56db] hover:underline" title={l.title}>{l.title}</Link></Cell>,
                                    price: <Cell><Txt bold mono>{money(l.price)}</Txt></Cell>,
                                    views: <Cell><Txt mono right>{l.views || 0}</Txt></Cell>,
                                    inquiries: <Cell><Txt mono right>{l.inquiry_count || 0}</Txt></Cell>,
                                    status: <Cell><Tag status={l.status} testid={`my-listing-status-${l.slug}`}>{l.status === "pending" ? "in review" : l.status}</Tag></Cell>,
                                    boost: (
                                        <Cell>
                                            {l.status !== "active" ? <Dash why="Only a live ad can be boosted" />
                                                : l.featured ? <Tag cls="border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv" testid={`boosted-${l.slug}`}><Zap size={9} /> boosted</Tag>
                                                    : <Btn onClick={() => boost(l)} testid={`boost-${l.slug}`}><Zap size={10} /> Boost $1.98</Btn>}
                                        </Cell>
                                    ),
                                    pin: (
                                        <Cell>
                                            {l.status !== "active" ? <Dash why="Only a live ad can be pinned" />
                                                : l.pinned ? <Tag status="rejected" testid={`pinned-${l.slug}`}><Pin size={9} /> on the board</Tag>
                                                    : <Btn onClick={() => act(() => api.post(`/marketplace/listings/${l.id}/pin`), "Pinned to the board for 24 hours")} testid={`pin-${l.slug}`}><Pin size={10} /> Pin 24h</Btn>}
                                        </Cell>
                                    ),
                                    actions: (
                                        <Cell className="justify-end">
                                            {l.status === "active" && <Btn onClick={() => status(l, "sold", "Marked as sold")} testid={`mark-sold-${l.slug}`}>Mark sold</Btn>}
                                            {l.status === "sold" && <Btn onClick={() => status(l, "active", "Listing live")} testid={`relist-${l.slug}`}>Relist</Btn>}
                                            <IconBtn onClick={() => status(l, "removed", "Listing removed")} testid={`remove-${l.slug}`} title="Remove this ad" danger><Trash2 size={12} /></IconBtn>
                                        </Cell>
                                    ),
                                })} />
                            {!listings.length && (
                                <div className="border-t border-[#d0d0d0] px-3 py-3 text-center" data-testid="my-listings-empty">
                                    <TagIcon size={22} className="mx-auto text-[#4a4a4a]" />
                                    <Link to="/supplies/sell" className="mt-2 inline-block">
                                        <Btn tone="solid" testid="my-listings-empty-post"><Plus size={11} /> post an ad</Btn>
                                    </Link>
                                </div>
                            )}
                        </Section>

                        <Section tone={TONE} pad={false} testid="my-inquiries" title={`${inquiries.length} buyer messages`}
                            note="they land here and in your email — your phone number stays private">
                            <Sheet cols={MSG_COLS} rows={inquiries} rowKey={(q) => q.id} testid="inquiries-sheet" minWidth={800}
                                empty="No messages yet." rowTestid={(q) => `my-inquiry-${q.id}`}
                                cells={(q) => ({
                                    who: <Cell><Two a={q.name} b={[q.email, q.phone].filter(Boolean).join(" · ")} /></Cell>,
                                    ad: <Cell><Link to={`/supplies/${q.listing_slug}`} className="truncate text-[12px] font-semibold text-[#1a56db] hover:underline">{q.listing_title}</Link></Cell>,
                                    message: <Cell><Txt sub title={q.message}>{q.message}</Txt></Cell>,
                                })} />
                            {!inquiries.length && <p className="sr-only" data-testid="my-inquiries-empty">No messages yet.</p>}
                        </Section>
                    </div>
                </>
            )}
        </BackOffice>
    );
}
